import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import {
    ClientConfigService,
    CookieName,
    CookieService,
    DeviceService,
    DslService,
    Logger,
    MediaQueryBreakpoint,
    MediaQueryService,
    MenuContentItem,
    UserEvent,
    UserLoginEvent,
    UserService,
} from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

import { AccountMenuConfig } from './account-menu.client-config';

/**
 * Service for managing and providing data for the account menu.
 * Handles menu configuration, content evaluation, widget updates, and cookie management.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class AccountMenuDataService {
    private readonly accountMenuConfig = inject(AccountMenuConfig);
    private readonly dslService = inject(DslService);
    private readonly user = inject(UserService);
    private readonly clientConfigService = inject(ClientConfigService);
    private readonly log = inject(Logger);
    private readonly deviceService = inject(DeviceService);
    private readonly cookieService = inject(CookieService);
    private readonly mediaQueryService = inject(MediaQueryService);
    private readonly router = inject(Router);

    /** Translated strings from sitecore related to the account menu. */
    readonly resources = toSignal(
        this.accountMenuConfig.whenReady.pipe(
            map(() =>
                this.accountMenuConfig.account.version === 1
                    ? this.accountMenuConfig.resources
                    : { messages: this.accountMenuConfig.account.root.resources },
            ),
        ),
    );
    /** VIP levels available in the account menu. */
    readonly vipLevels = toSignal(this.accountMenuConfig.whenReady.pipe(map(() => this.accountMenuConfig.vipLevels)));
    /** Version of the account menu configuration. */
    readonly version = toSignal(this.accountMenuConfig.whenReady.pipe(map(() => this.accountMenuConfig.account.version)));
    /** Position of profile page items in the menu. */
    readonly profilePageItemsPosition = toSignal(
        this.accountMenuConfig.whenReady.pipe(map(() => this.accountMenuConfig.account.profilePageItemsPosition)),
    );
    /** Emits when top menu items are loaded. */
    readonly topItemsLoaded: BehaviorSubject<MenuContentItem[]> = new BehaviorSubject<MenuContentItem[]>([]);

    private itemHierarchy: any;
    private singlePageModeEnabled = false;
    private topItems: MenuContentItem[] = [];
    private readonly lookup = new Map<string, MenuContentItem>();
    private readonly onWidgetUpdate: Subject<void> = new Subject();
    private readonly contentEvents = new BehaviorSubject<MenuContentItem | null>(null);
    private readonly unsubscribe = new Subject<void>();

    /**
     * Observable that emits when a widget item is updated.
     */
    get widgetUpdate(): Observable<void> {
        return this.onWidgetUpdate;
    }

    /**
     * Indicates if the current device is desktop.
     */
    get isDesktop(): boolean {
        return this.mediaQueryService.isActive(MediaQueryBreakpoint.GtSm);
    }

    /**
     * Indicates that the menu will operate in router mode (e.g. open as a route rather than overlay).
     */
    get routerMode(): boolean {
        return this.deviceService.isMobilePhone;
    }

    /**
     * Indicates if the menu is open as a routable overlay (e.g. on mobile).
     */
    get isRoutableOverlay(): boolean {
        return this.router.url.includes(':menu');
    }

    /**
     * Gets the return URL for the account menu from the cookie.
     *
     * @returns The return URL string stored in the VnMenuReturnUrl cookie.
     */
    get routerModeReturnUrl(): string {
        return this.cookieService.get(CookieName.VnMenuReturnUrl);
    }

    /**
     * Returns the hierarchy of menu items.
     */
    get hierarchy(): any {
        return this.itemHierarchy;
    }

    /**
     * Observable that emits the current menu content item.
     */
    get content(): Observable<MenuContentItem> {
        return this.contentEvents.pipe(
            filter(Boolean),
            distinctUntilChanged((previous: MenuContentItem, current: MenuContentItem) => JSON.stringify(previous) === JSON.stringify(current)),
        );
    }

    /**
     * Returns the current top menu items.
     */
    get topMenuItems(): MenuContentItem[] {
        return this.topItems;
    }

    /**
     * Indicates whether single page mode is enabled.
     */
    get singlePageMode(): boolean {
        return this.singlePageModeEnabled;
    }

    /**
     * Sets single page mode enabled or disabled.
     * @param singlePageMode Whether single page mode should be enabled.
     */
    set singlePageMode(singlePageMode: boolean) {
        this.singlePageModeEnabled = singlePageMode;
    }

    /**
     * Initializes the account menu data service.
     * Sets up subscriptions for content evaluation, user login events, and top menu items loading.
     */
    init() {
        this.dslService
            .evaluateContent(this.accountMenuConfig.account.root)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((c: MenuContentItem) => this.forceRefresh(c));

        this.user.events.pipe(first((e: UserEvent) => e instanceof UserLoginEvent)).subscribe(() =>
            this.clientConfigService.reload([AccountMenuConfig]).then(() => {
                this.unsubscribe.next();
                this.unsubscribe.complete();
                this.forceRefresh(this.accountMenuConfig.account.root);
                this.dslService.evaluateContent(this.accountMenuConfig.account.root).subscribe((c: MenuContentItem) => this.forceRefresh(c));
            }),
        );

        this.topItemsLoaded
            .pipe(
                distinctUntilChanged(
                    (previous: MenuContentItem[], current: MenuContentItem[]) => JSON.stringify(previous) === JSON.stringify(current),
                ),
                switchMap((topItems: MenuContentItem[]) => {
                    this.topItems = []; //Clear list before evaluating new content to avoid old items showing up till evaluation happens.
                    return this.dslService.evaluateContent(topItems);
                }),
            )
            .subscribe((items: MenuContentItem[]) => {
                this.topItems = items;
            });
    }

    /**
     * Refreshes account menu widgets section.
     */
    refreshWidgets() {
        this.onWidgetUpdate.next();
    }

    /**
     * Sets the menu's return URL cookie to the supplied URL value.
     * @param url The URL to set in the cookie.
     */
    setReturnUrlCookie(url: string) {
        this.cookieService.put(CookieName.VnMenuReturnUrl, url);
    }

    /**
     * Removes the menu return URL cookie.
     */
    removeReturnUrlCookie() {
        this.cookieService.remove(CookieName.VnMenuReturnUrl);
    }

    /**
     * Gets a menu content item by name.
     * @param name The name of the menu item.
     * @returns The menu content item or null if not found.
     */
    getItem(name: string): MenuContentItem | null {
        return this.lookup.get(name) || null;
    }

    /**
     * Forces a refresh of the menu content and hierarchy.
     * @param c The root menu content item.
     * @private
     */
    private forceRefresh(c: MenuContentItem) {
        this.lookup.clear();
        this.itemHierarchy = {};

        this.initMenu(c, this.itemHierarchy);
        this.contentEvents.next(c);
    }

    /**
     * Initializes the menu hierarchy and lookup map recursively.
     * Warns if duplicate item names are found.
     * @param item The current menu content item.
     * @param hierarchy The hierarchy object to populate.
     * @private
     */
    private initMenu(item: MenuContentItem, hierarchy: any) {
        if (this.lookup.has(item.name)) {
            this.log.warn(`Menu contains multiple items with the same name: ${item.name}`);
        }

        this.lookup.set(item.name, item);

        if (item.children) {
            const childHierarchy = {};
            item.children.forEach((i: MenuContentItem) => this.initMenu(i, childHierarchy));
            hierarchy[item.name] = childHierarchy;
        } else {
            hierarchy[item.name] = null;
        }
    }
}
